/**
 * Photos and video, on the client's own page (P5b).
 *
 * ---------------------------------------------------------------------------
 * TWO FILE PICKERS, AND NO IN-PORTAL RECORDER
 * ---------------------------------------------------------------------------
 * There was one: MediaRecorder at 720p/2.5 Mbps, to keep a walkthrough near
 * 19 MB a minute where a phone's own camera app produces four to eight times
 * that. It was removed after its first real test, and the reason is worth keeping
 * written down.
 *
 * The preview was black. The <video> element only existed while `recording` was
 * true, so the ref was still null when the stream was assigned to it, and the
 * guard around that assignment swallowed the miss in silence. A three-line fix —
 * but it exposed how much of this depended on MediaRecorder behaving the same way
 * on a phone as on a laptop, which on iOS Safari it does not: container support
 * moves with point releases, and every failure mode is invisible until someone
 * films their house and loses it.
 *
 * NEITHER PICKER SETS `capture`. The attribute forces the camera and hides the
 * camera roll; without it the phone offers both and the client picks, which is
 * what someone who already filmed the garage needs.
 *
 * WHAT WE GIVE UP: a client filming fresh video in their camera app may produce a
 * file far over the cap. The size check is therefore the first thing that happens
 * to a chosen file, and its message names the remedy — a shorter clip — because
 * there is no longer a smaller way to film the same walkthrough here.
 *
 * ---------------------------------------------------------------------------
 * TWO UPLOAD PATHS, BECAUSE THE SERVER HAS TWO
 * ---------------------------------------------------------------------------
 * A photo is one POST of its bytes to our API. A video is prepare -> PUT
 * straight to storage -> commit, so 200 MB never travels through the API
 * container. See backend/src/routes/portal.ts.
 *
 * EVERY CAP IS ENFORCED ON THE SERVER. The copies here exist to stop an upload
 * that cannot succeed before it starts and to phrase the reason kindly; the
 * server's message wins whenever it disagrees, and is what gets displayed.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Icons } from '../../lib/icons';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/** Mirrors MEDIA_CAPS in backend/src/lib/inventoryMedia.ts, which enforces them. */
export const CAPS = {
  photo: { maxCount: 15, maxBytes: 15 * 1024 * 1024 },
  video: { maxCount: 3, maxBytes: 200 * 1024 * 1024 },
};

export interface ClientMedia {
  id: string;
  kind: 'photo' | 'video';
  url: string;
  downloadUrl: string;
  caption: string | null;
  mime: string;
  sizeBytes: number;
  originalName: string | null;
  roomKey: string | null;
  recordedInPortal: boolean;
  mine: boolean;
  createdAt: string;
}

function fmtBytes(n: number): string {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

type Job = { key: string; name: string; kind: 'photo' | 'video'; pct: number };

export function MediaSection({ media, token, readOnly, onChanged }: {
  media: ClientMedia[];
  token: string;
  readOnly?: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [err, setErr] = useState('');
  const [lightbox, setLightbox] = useState<ClientMedia | null>(null);

  const photoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);

  const photos = media.filter((m) => m.kind === 'photo');
  const videos = media.filter((m) => m.kind === 'video');
  const photoRoom = CAPS.photo.maxCount - photos.length;
  const videoRoom = CAPS.video.maxCount - videos.length;

  const api = (path: string) => `${API_BASE_URL}/portal/inventory/${encodeURIComponent(token)}${path}`;

  const failFrom = async (res: Response, fallback: string) => {
    const json = await res.json().catch(() => null);
    return json?.error?.message || fallback;
  };

  // ---- photo: one POST of the bytes ---------------------------------------
  const uploadPhoto = async (file: File) => {
    const key = `${file.name}-${file.size}-${Math.random()}`;
    setJobs((j) => [...j, { key, name: file.name, kind: 'photo', pct: 0 }]);
    try {
      const res = await fetch(api(`/media?name=${encodeURIComponent(file.name)}`), {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) throw new Error(await failFrom(res, 'We could not add that photo.'));
    } finally {
      setJobs((j) => j.filter((x) => x.key !== key));
    }
  };

  /**
   * PUT with XHR rather than fetch, for one reason: fetch cannot report upload
   * progress. A client sending 40 MB over a phone signal with no progress bar
   * concludes it has hung and leaves the page.
   */
  const putWithProgress = (url: string, body: Blob, key: string) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      if (body.type) xhr.setRequestHeader('content-type', body.type);
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const pct = Math.round((e.loaded / e.total) * 100);
        setJobs((j) => j.map((x) => (x.key === key ? { ...x, pct } : x)));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error('The upload did not finish. Please try again.')));
      xhr.onerror = () => reject(new Error('The upload stopped before it finished. Please check your signal and try again.'));
      xhr.onabort = () => reject(new Error('Upload cancelled.'));
      xhr.send(body);
    });

  // ---- video: prepare -> PUT to storage -> commit --------------------------
  const uploadVideo = async (blob: Blob, name: string) => {
    const key = `${name}-${blob.size}-${Math.random()}`;
    setJobs((j) => [...j, { key, name, kind: 'video', pct: 0 }]);
    try {
      const prep = await fetch(api('/media/prepare'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, mime: blob.type || 'video/mp4', size_bytes: blob.size }),
      });
      if (!prep.ok) throw new Error(await failFrom(prep, 'We could not start that upload.'));
      const { data } = await prep.json();

      await putWithProgress(data.uploadUrl, blob, key);

      const commit = await fetch(api(`/media/${data.id}/commit`), { method: 'POST' });
      if (!commit.ok) throw new Error(await failFrom(commit, 'We could not finish that upload.'));
    } finally {
      setJobs((j) => j.filter((x) => x.key !== key));
    }
  };

  const runUploads = async (files: File[], kind: 'photo' | 'video') => {
    setErr('');
    const room = kind === 'photo' ? photoRoom : videoRoom;
    if (room <= 0) {
      setErr(kind === 'photo'
        ? `That's the most photos we can take (${CAPS.photo.maxCount}). Remove one to add another.`
        : `That's the most videos we can take (${CAPS.video.maxCount}). Remove one to add another.`);
      return;
    }
    const take = files.slice(0, room);
    if (files.length > take.length) {
      setErr(`We took the first ${take.length} — that's all the room left.`);
    }

    // ONE AT A TIME, deliberately. Four parallel uploads on one phone signal are
    // four slow uploads, and the progress figures become meaningless.
    for (const file of take) {
      const cap = kind === 'photo' ? CAPS.photo.maxBytes : CAPS.video.maxBytes;
      if (file.size > cap) {
        setErr(kind === 'photo'
          ? `“${file.name}” is ${fmtBytes(file.size)}. Photos need to be under ${fmtBytes(cap)}.`
          : `“${file.name}” is ${fmtBytes(file.size)}. Videos need to be under ${fmtBytes(cap)} — record one here instead, or send a shorter clip.`);
        continue;
      }
      try {
        if (kind === 'photo') await uploadPhoto(file);
        else await uploadVideo(file, file.name);
      } catch (e: any) {
        setErr(e?.message || 'That upload did not work. Please try again.');
        break;
      }
    }
    await onChanged();
  };

  const saveCaption = async (m: ClientMedia, caption: string) => {
    if ((m.caption ?? '') === caption) return;
    try {
      const res = await fetch(api(`/media/${m.id}`), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption }),
      });
      if (!res.ok) throw new Error(await failFrom(res, 'We could not save that caption.'));
      await onChanged();
    } catch (e: any) { setErr(e?.message || 'We could not save that caption.'); }
  };

  const remove = async (m: ClientMedia) => {
    if (!window.confirm(m.kind === 'video' ? 'Remove this video?' : 'Remove this photo?')) return;
    try {
      const res = await fetch(api(`/media/${m.id}`), { method: 'DELETE' });
      if (!res.ok) throw new Error(await failFrom(res, 'We could not remove that.'));
      await onChanged();
    } catch (e: any) { setErr(e?.message || 'We could not remove that.'); }
  };

  // ---- read-only (submitted) ----------------------------------------------
  if (readOnly) {
    if (!media.length) return null;
    return (
      <div className="pp-card">
        <div className="pp-sec">Photos &amp; video</div>
        <MediaGrid media={media} onOpen={setLightbox} />
        {lightbox && <Lightbox item={lightbox} onClose={() => setLightbox(null)} />}
      </div>
    );
  }

  return (
    <div className="pp-card">
      <div className="pp-sec">Photos &amp; video</div>
      <p className="pp-sub" style={{ marginBottom: 12 }}>
        Not required — your list is enough. But pictures of the rooms, or a short walk-through
        video, tell us more about access and packing than any list can.
      </p>

      <div className="ci-media-acts">
        <button
          type="button" className="pp-btn" disabled={photoRoom <= 0}
          onClick={() => photoInput.current?.click()}
        >
          <Icons.box size={16} aria-hidden="true" /> Add photos
        </button>
        <button
          type="button" className="pp-btn" disabled={videoRoom <= 0}
          onClick={() => videoInput.current?.click()}
        >
          <Icons.document size={16} aria-hidden="true" /> Add video
        </button>
      </div>

      {/* NO `capture` ATTRIBUTE on either, deliberately: it forces the camera and
          hides the camera roll. Without it a phone offers both and the client
          chooses, which is the whole point of these two buttons. */}
      <input
        ref={photoInput} type="file" accept="image/*,.heic,.heif" multiple hidden
        onChange={(e) => { const f = Array.from(e.target.files ?? []); e.target.value = ''; if (f.length) runUploads(f, 'photo'); }}
      />
      <input
        ref={videoInput} type="file" accept="video/*,.mov,.mp4" multiple hidden
        onChange={(e) => { const f = Array.from(e.target.files ?? []); e.target.value = ''; if (f.length) runUploads(f, 'video'); }}
      />

      <div className="ci-media-h">
        {photos.length} of {CAPS.photo.maxCount} photos · {videos.length} of {CAPS.video.maxCount} videos
        {' '}· a video can be up to {fmtBytes(CAPS.video.maxBytes)}, and a shorter clip is easier to send
      </div>

      {jobs.map((j) => (
        <div key={j.key} className="ci-up">
          <div className="ci-up-n">{j.name}</div>
          <div className="ci-up-t">
            <div className="ci-up-f" style={{ width: `${j.kind === 'photo' ? 100 : j.pct}%` }} />
          </div>
          <div className="ci-up-p">{j.kind === 'photo' ? 'Sending…' : `${j.pct}%`}</div>
        </div>
      ))}

      {err && <div className="ci-state err" style={{ marginTop: 8 }}>{err}</div>}

      {media.length > 0 && (
        <MediaGrid
          media={media}
          onOpen={setLightbox}
          onCaption={saveCaption}
          onRemove={remove}
        />
      )}

      {lightbox && <Lightbox item={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function MediaGrid({ media, onOpen, onCaption, onRemove }: {
  media: ClientMedia[];
  onOpen: (m: ClientMedia) => void;
  onCaption?: (m: ClientMedia, v: string) => void;
  onRemove?: (m: ClientMedia) => void;
}) {
  return (
    <div className="ci-mgrid">
      {media.map((m) => (
        <div key={m.id} className="ci-mtile">
          <button type="button" className="ci-mthumb" onClick={() => onOpen(m)} aria-label={m.kind === 'video' ? 'Play video' : 'View photo'}>
            {m.kind === 'video'
              /* No <video> here: a grid of eight video elements each fetches its
                 own metadata, and on a phone that is eight parallel requests for
                 a poster frame nobody asked for. */
              ? <span className="ci-mvid"><Icons.play size={22} aria-hidden="true" /></span>
              : <img src={m.url} alt={m.caption ?? ''} loading="lazy" />}
          </button>
          <div className="ci-mmeta">
            {m.kind === 'video' ? 'Video' : 'Photo'}{m.sizeBytes ? ` · ${fmtBytes(m.sizeBytes)}` : ''}
          </div>
          {onCaption && (
            <input
              className="ci-mcap" defaultValue={m.caption ?? ''} placeholder="Add a note (optional)"
              onBlur={(e) => onCaption(m, e.target.value.trim())}
            />
          )}
          {onRemove && m.mine && (
            <button type="button" className="ci-mrm" onClick={() => onRemove(m)}>Remove</button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Full size, in place. A photo opens as an image and a video as a player with
 * controls — and with `playsInline`, without which iOS takes a tap on a video as
 * a request for its own fullscreen player and leaves the page behind.
 */
function Lightbox({ item, onClose }: { item: ClientMedia; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ci-lb" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ci-lb-in" onClick={(e) => e.stopPropagation()}>
        {item.kind === 'video'
          ? <video src={item.url} controls playsInline autoPlay className="ci-lb-m" />
          : <img src={item.url} alt={item.caption ?? ''} className="ci-lb-m" />}
        <div className="ci-lb-bar">
          <span className="ci-lb-c">{item.caption || item.originalName || (item.kind === 'video' ? 'Video' : 'Photo')}</span>
          <a className="pp-btn" href={item.downloadUrl}>
            <Icons.download size={16} aria-hidden="true" /> Download
          </a>
          <button type="button" className="pp-btn pp-btn-quiet" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
